import { LoadingButton } from '@zarya/ui';
import type { ApiError } from '@app/api/ApiError';
import { t } from '@app/i18n';
import { useCanOperar } from '@app/auth/useCan';
import { useDiscoveryNow } from '@app/discovery';

/**
 * **Discovery agora** (FR-039, FR-040) — pede um tique da varredura e responde na
 * hora.
 *
 * Compartilhado entre Identidades (T4) e o card Discovery de T21. A Visão geral
 * (T3) tem o seu, com o mesmo `useDiscoveryAgora` por baixo — é o hook, e não o
 * componente, que é a peça compartilhada desde 29/08.
 *
 * Papel: `runDiscovery` é admin/approver — o `viewer` **não vê** o botão (FR-010
 * manda omitir, não desabilitar).
 *
 * O botão fica ocupado enquanto **houver varredura** — a deste clique ou a de
 * outra pessoa —, e não só enquanto o `POST` está em voo: o `POST` responde em
 * milissegundos e outro clique só ganharia um `409`. Quem conta os 20–40 s é o
 * indicador da topbar, sem travar a tela (29/08).
 */

export interface DiscoveryNowButtonProps {
  /**
   * Erro do **disparo**, para a tela mostrar o banner com o código (FR-040).
   * Chamado com `null` quando um novo clique começa bem — o banner anterior sai.
   */
  onErro?: (erro: ApiError | null) => void;
  /**
   * Aviso da varredura **terminada** (`error_code` ou o primeiro `warnings[]`).
   * Desde 29/08 é por aqui que a falha do executor aparece: a varredura corre
   * fora da requisição e não tem mais como voltar num 502.
   */
  onAviso?: (aviso: string | null) => void;
}

export function DiscoveryNowButton({ onErro, onAviso }: DiscoveryNowButtonProps) {
  const can = useCanOperar('runDiscovery');
  const discovery = useDiscoveryNow({ onErro, onAviso });

  if (!can) return null;

  return (
    <LoadingButton
      variant="out"
      icon="refresh"
      loading={discovery.disparando || discovery.emExecucao}
      loadingLabel={t('common.discoveryNow.loading')}
      onClick={() => void discovery.executar()}
    >
      {t('common.discoveryNow.label')}
    </LoadingButton>
  );
}
